import React from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import FormDrawer from "../../components/layout/FormDrawer";
import { BRAND } from "../../components/auth/authTheme";

const DRAWER_WIDTH = { xs: "100%", sm: 600, md: 820 };

const formatCurrency = (val) => {
  if (val === null || val === undefined || val === "") return "N/A";
  const num = Number(val);
  if (Number.isNaN(num)) return "N/A";
  return `₹${num.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
};

const DetailItem = ({ icon, label, value, fullWidth = false, compact = false }) => (
  <Box
    sx={{
      display: "flex",
      gap: compact ? 1.25 : 2,
      p: compact ? 1.25 : 2,
      borderRadius: compact ? "10px" : "12px",
      border: `1px solid ${BRAND.border}`,
      bgcolor: "#fff",
      gridColumn: fullWidth ? "1 / -1" : undefined,
    }}
  >
    <Box
      sx={{
        width: compact ? 34 : 40,
        height: compact ? 34 : 40,
        borderRadius: compact ? "8px" : "10px",
        bgcolor: BRAND.orangeLight,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        color: BRAND.orange,
        "& .MuiSvgIcon-root": { fontSize: compact ? "1rem" : "1.25rem" },
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography
        variant="caption"
        sx={{
          color: BRAND.muted,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontSize: compact ? "0.625rem" : "0.7rem",
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color: value && value !== "N/A" ? BRAND.navy : BRAND.muted,
          fontWeight: value && value !== "N/A" ? 600 : 400,
          mt: 0.25,
          wordBreak: "break-word",
          fontSize: compact ? "0.875rem" : "1rem",
        }}
      >
        {value || "N/A"}
      </Typography>
    </Box>
  </Box>
);

const Section = ({ title, children, compact = false }) => (
  <Box sx={{ mb: compact ? 2 : 4 }}>
    <Typography
      variant="h6"
      sx={{
        mb: compact ? 1.25 : 2,
        color: BRAND.navy,
        fontWeight: 700,
        fontSize: compact ? "0.9375rem" : "1.125rem",
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const EmployeePayrollView = ({ show, handleClose, payroll }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  if (!payroll) return null;

  const {
    emp_id,
    employee_name,
    basic_salary,
    hra,
    bonus,
    deductions,
    salary = {},
    challans = [],
    other_allowances,
  } = payroll;

  const renderTwoColumnList = (obj) => {
    if (!obj || typeof obj !== "object" || Object.keys(obj).length === 0) return "N/A";
    return Object.entries(obj)
      .map(([key, value]) => `${key}: ₹${value ?? "N/A"}`)
      .join(", ");
  };

  const renderChallans = (challanList) => {
    if (!challanList?.length) return "N/A";
    return challanList.map((c) => c.name).join(", ");
  };

  return (
    <FormDrawer
      open={show}
      onClose={handleClose}
      width={DRAWER_WIDTH}
      title={isMobile ? employee_name || "Employee" : "Employee Payroll Details"}
      subtitle={
        isMobile
          ? emp_id || "Salary configuration"
          : `Payroll for ${employee_name || "Employee"}`
      }
    >
      <Box
        sx={{
          p: isMobile ? 2 : 3,
          pt: isMobile ? 0.5 : 1,
          display: "flex",
          flexDirection: "column",
          gap: isMobile ? 2 : 3,
          bgcolor: isMobile ? BRAND.bg : undefined,
        }}
      >
        {isMobile && (
          <Box
            sx={{
              bgcolor: BRAND.orangeLight,
              borderRadius: "12px",
              border: `1px solid ${BRAND.orange}33`,
              p: 1.5,
              textAlign: "center",
            }}
          >
            <Typography sx={{ fontSize: "0.6875rem", color: BRAND.muted, fontWeight: 600 }}>
              NET SALARY
            </Typography>
            <Typography sx={{ fontSize: "1.5rem", fontWeight: 800, color: BRAND.navy, lineHeight: 1.2 }}>
              {formatCurrency(salary?.net_salary)}
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: BRAND.muted, mt: 0.25 }}>
              Gross {formatCurrency(salary?.gross_salary)}
            </Typography>
          </Box>
        )}

        {!isMobile && (
          <Section title="Employee Information">
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              <DetailItem icon={<PersonOutlineIcon />} label="Employee Name" value={employee_name} />
              <DetailItem icon={<BusinessOutlinedIcon />} label="Employee ID" value={emp_id} />
            </Box>
          </Section>
        )}

        <Section title="Salary Details" compact={isMobile}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : { xs: "1fr", sm: "1fr 1fr" },
              gap: isMobile ? 1 : 2,
            }}
          >
            <DetailItem
              icon={<AttachMoneyOutlinedIcon />}
              label="Basic Salary"
              value={formatCurrency(basic_salary)}
              compact={isMobile}
            />
            <DetailItem
              icon={<AttachMoneyOutlinedIcon />}
              label="HRA"
              value={formatCurrency(hra)}
              compact={isMobile}
            />
            <DetailItem
              icon={<AttachMoneyOutlinedIcon />}
              label="Bonus"
              value={formatCurrency(bonus)}
              compact={isMobile}
            />
            <DetailItem
              icon={<AccountBalanceOutlinedIcon />}
              label="Gross Salary"
              value={formatCurrency(salary?.gross_salary)}
              compact={isMobile}
            />
            {!isMobile && (
              <DetailItem
                icon={<AccountBalanceOutlinedIcon />}
                label="Net Salary"
                value={formatCurrency(salary?.net_salary)}
              />
            )}
          </Box>
        </Section>

        <Section title="Additional Information" compact={isMobile}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: isMobile ? 1 : 2,
            }}
          >
            <DetailItem
              icon={<AttachMoneyOutlinedIcon />}
              label="Other Allowances"
              value={renderTwoColumnList(other_allowances)}
              fullWidth
              compact={isMobile}
            />
            <DetailItem
              icon={<AttachMoneyOutlinedIcon />}
              label="Deductions"
              value={renderTwoColumnList(deductions)}
              fullWidth
              compact={isMobile}
            />
            <DetailItem
              icon={<ReceiptLongOutlinedIcon />}
              label="Challans"
              value={renderChallans(challans)}
              fullWidth
              compact={isMobile}
            />
          </Box>
        </Section>
      </Box>
    </FormDrawer>
  );
};

export default EmployeePayrollView;
